/**
 * Backup configuration resolver + selectors (plan 1.3 / contract C1).
 *
 * The user document carries an all-optional `backup` object. Every
 * consumer (capture gating, uploader, scheduler, banner, settings UI)
 * needs the SAME fully-defaulted view of it — this module is that single
 * resolver. Deliberately self-contained (no cross-repo import); the
 * backend defines an equivalent resolver locally.
 *
 * Two run contexts share the same fields with different defaults:
 *   - EXTENSION (the user's own WhatsApp Web tab): daily interval in HOURS,
 *     idle/uptime-gated, opt-in (`dbBackupEnabled` default false).
 *   - GATEWAY (a dedicated 24/7 pod — `getClientType()===GATEWAY`): frequent
 *     interval in MINUTES (default 15), no idle/uptime gate, OFF by default.
 * A debug `testIntervalMinutes` override forces the interval (and server-mode
 * run-rules) in ANY context for fast testing.
 */

/** Which media BYTES are eligible for the bucket. */
export interface ResolvedBackupMedia {
  audio: boolean;
  images: boolean;
  documents: boolean;
  videos: boolean;
  archives: boolean;
  /** Per-file cap in MB; larger files back up as metadata only. */
  maxMb: number;
}

/** Fully-defaulted backup config for the ACTIVE context. */
export interface ResolvedBackupConfig {
  vaultEnabled: boolean;
  dbBackupEnabled: boolean;
  vaultRetentionDays: number;
  /** Canonical interval, in minutes (extension = hours×60, gateway = minutes). */
  intervalMinutes: number;
  /** Extension-only display value (hours); gateway leaves it at intervalMinutes/60. */
  backupIntervalHours: number;
  mediaTtlDays: number;
  media: ResolvedBackupMedia;
  ui: { showCountdown: boolean };
  /** Server mode = gateway pod OR test override: skip idle/uptime gate + banner. */
  serverMode: boolean;
}

/** Raw gateway sub-config — mirrors the extension fields, minutes interval. */
export interface RawGatewayBackupConfig {
  vaultEnabled?: boolean;
  dbBackupEnabled?: boolean;
  vaultRetentionDays?: number;
  backupIntervalMinutes?: number;
  mediaTtlDays?: number;
  media?: {
    audio?: boolean;
    images?: boolean;
    documents?: boolean;
    videos?: boolean;
    archives?: boolean;
    maxMb?: number;
  };
}

/** Raw (all-optional) input shape — mirrors `UserInterface['backup']`.
 *  Declared locally so this module has no dependency on the user model. */
export interface RawBackupConfig {
  vaultEnabled?: boolean;
  dbBackupEnabled?: boolean;
  vaultRetentionDays?: number;
  backupIntervalHours?: number;
  mediaTtlDays?: number;
  media?: {
    audio?: boolean;
    images?: boolean;
    documents?: boolean;
    videos?: boolean;
    archives?: boolean;
    maxMb?: number;
  };
  ui?: { showCountdown?: boolean };
  lastBackupAt?: number | string;
  /** Debug "Back up now": ms epoch stamped by the settings page to trigger a
   *  one-off engine backup (bypasses idle/due gates). 0/undefined = none. */
  forceRunAt?: number;
  /** Debug: force the interval (minutes) in any context; 0/undefined = off. */
  testIntervalMinutes?: number;
  /** Dedicated-server (gateway pod) overrides — 15-min interval, OFF by default. */
  gateway?: RawGatewayBackupConfig;
}

const bool = (v: boolean | undefined, dflt: boolean): boolean =>
  typeof v === 'boolean' ? v : dflt;

const num = (v: number | undefined, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : dflt;

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

export const VAULT_RETENTION_MIN_DAYS = 2;
export const VAULT_RETENTION_MAX_DAYS = 30;

/**
 * Backup config default VALUES — the single source of truth shared by the
 * resolvers here AND the settings UI (`account-backup.tsx`) so the two can't
 * drift (the UI is an editor and needs a different output shape, but must use
 * the same defaults). Note both `dbBackupEnabled` flags default OFF — nothing
 * auto-backs-up until explicitly enabled.
 */
export const BACKUP_DEFAULTS = {
  vaultEnabled: true,
  dbBackupEnabled: false,            // extension: opt-in
  vaultRetentionDays: 2,
  backupIntervalHours: 24,
  mediaTtlDays: 7,
  showCountdown: true,
  media: { audio: true, images: true, documents: true, videos: false, archives: false, maxMb: 15 },
  gateway: {
    vaultEnabled: true,
    dbBackupEnabled: false,          // gateway: opt-in (admin), was ON — flipped
    vaultRetentionDays: 7,
    backupIntervalMinutes: 15,
    mediaTtlDays: 7,
  },
} as const;

/** Resolve a raw media sub-config to fully-defaulted booleans + a clamped
 *  cap. Exported so the settings UI shares ONE media-default definition. */
export const resolveMedia = (
  m: RawBackupConfig['media'] | RawGatewayBackupConfig['media'],
): ResolvedBackupMedia => {
  const media = m ?? {};
  const d = BACKUP_DEFAULTS.media;
  return {
    audio: bool(media.audio, d.audio),
    images: bool(media.images, d.images),
    documents: bool(media.documents, d.documents),
    videos: bool(media.videos, d.videos),
    archives: bool(media.archives, d.archives),
    maxMb: Math.max(1, num(media.maxMb, d.maxMb)),
  };
};

/** EXTENSION context — user's own tab. Daily (hours), opt-in, banner. */
export const resolveBackupConfig = (
  raw?: RawBackupConfig | null,
): ResolvedBackupConfig => {
  const r = raw ?? {};
  const d = BACKUP_DEFAULTS;
  const hours = Math.max(1, num(r.backupIntervalHours, d.backupIntervalHours));
  return {
    vaultEnabled: bool(r.vaultEnabled, d.vaultEnabled),
    dbBackupEnabled: bool(r.dbBackupEnabled, d.dbBackupEnabled),
    // Default 2 days — local vault for deleted-message recovery (matches the
    // pre-backup behavior). Server DB backup is a separate opt-in.
    vaultRetentionDays: clamp(num(r.vaultRetentionDays, d.vaultRetentionDays), VAULT_RETENTION_MIN_DAYS, VAULT_RETENTION_MAX_DAYS),
    intervalMinutes: hours * 60,
    backupIntervalHours: hours,
    mediaTtlDays: Math.max(1, num(r.mediaTtlDays, d.mediaTtlDays)),
    media: resolveMedia(r.media),
    ui: { showCountdown: bool(r.ui?.showCountdown, d.showCountdown) },
    serverMode: false,
  };
};

/** GATEWAY context — dedicated pod. Frequent (minutes), OFF by default now
 *  (admin opts a gateway in), headless (no banner), no idle/uptime gate. */
export const resolveGatewayBackupConfig = (
  raw?: RawBackupConfig | null,
): ResolvedBackupConfig => {
  const g = raw?.gateway ?? {};
  const d = BACKUP_DEFAULTS.gateway;
  const minutes = Math.max(1, num(g.backupIntervalMinutes, d.backupIntervalMinutes));
  return {
    vaultEnabled: bool(g.vaultEnabled, d.vaultEnabled),
    dbBackupEnabled: bool(g.dbBackupEnabled, d.dbBackupEnabled),
    vaultRetentionDays: clamp(num(g.vaultRetentionDays, d.vaultRetentionDays), VAULT_RETENTION_MIN_DAYS, VAULT_RETENTION_MAX_DAYS),
    intervalMinutes: minutes,
    backupIntervalHours: minutes / 60,
    mediaTtlDays: Math.max(1, num(g.mediaTtlDays, d.mediaTtlDays)),
    media: resolveMedia(g.media),
    ui: { showCountdown: false },
    serverMode: true,
  };
};

/** True when running on a dedicated gateway pod. Mirrors detect-context's
 *  `getClientType()===GATEWAY` rule (a gateway session sets `localStorage.
 *  sessionId`) — inlined so this pure-config module doesn't pull the app-config
 *  chain into unit tests. */
export const isGatewayContext = (): boolean => {
  try {
    return typeof localStorage !== 'undefined' && !!localStorage.getItem('sessionId');
  } catch {
    return false;
  }
};

/** Resolve the config for the ACTIVE context, applying the debug test-interval
 *  override (which forces the interval AND server-mode rules everywhere). */
export const resolveEffectiveBackupConfig = (
  raw?: RawBackupConfig | null,
  gateway: boolean = isGatewayContext(),
): ResolvedBackupConfig => {
  const base = gateway ? resolveGatewayBackupConfig(raw) : resolveBackupConfig(raw);
  const testMin = num(raw?.testIntervalMinutes, 0);
  if (testMin > 0) {
    return { ...base, intervalMinutes: testMin, serverMode: true };
  }
  return base;
};

// Redux selectors that read the user document live in `backup-config-selectors.ts`
// (they import the reducer chain). This module stays pure so the resolvers can be
// unit-tested without pulling app-config/analytics into the jest env.
